import React, {FC, useMemo, useState, useCallback} from "react";

// eslint-disable-next-line node/no-extraneous-import
import DOMPurify from "dompurify";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

type componentProps = {
  value:string,
  type?:string,
  attrs?:{[key:string]:any}, // only to get key-property
  id?:string,
  style?:React.CSSProperties | CSS.Properties,
  color?:string,
  backgroundColor?:string,
  className?:string,
  children?:React.ReactNode, // | React.ReactElement<any> | React.CSSProperties
  hasStyleSizeVariable?:boolean
} & {[key:string]:any}; // string | boolean | number | React.ReactNode

type codeType = "html" | "md" | "txt";

const {$, rz} = window;

const cleanHtml = (value) => (DOMPurify.sanitize(value, {
  USE_PROFILES: { html: true, svg: true, svgFilters: true }, // Разрешает только HTML-теги
  ADD_ATTR: ["target", "onclick", "onClick", "uk-tooltip"] // , "filename", "fileurl", "fileplace"
  // ALLOWED_TAGS: ["p", "br", "a", "span", "div", "ol", "ul", "li", "strong", "em", "table", "tbody", "thead", "tr", "td", "img", "svg", "g", "path", "polygon", "circle", "line"] // Какие теги оставить
}));

const HTMLMDviewer:FC<componentProps> = (props) => {
  // const customStyle = (props.style ? {...props.style} : undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [theme, setTheme] = useState($("html").attr("data-theme"));

  const styl = useMemo<React.CSSProperties | CSS.Properties>(() => {
    const est:React.CSSProperties | CSS.Properties = {...props.style};
    let clr:string; const allc = (props.color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (props.backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [props, theme]);

  const codeTp:codeType = useMemo(() => {
    if (props.value && rz.isString(props.value)) {
      if (props.type) {
        if (props.type === "html") { return "html" }
        if (rz.arrContains(["md", "markdown"], props.type, true, true)) { return "md" }
      }
      if (props.value.trim().startsWith("<") && props.value.trim().endsWith(">")) { return "html" }
    }
    return "txt";
  }, [props.type, props.value]);

  const codeComponents = useMemo(() => ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    code({ node, inline, className, children, ...props1 }: any) {
      const match = /language-(\w+)/.exec(className || "");
      return !inline && match ? (
        // style={theme === "light" ? light : dark}
        <SyntaxHighlighter PreTag="div" language={match[1]} {...props1}>
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props1}>
          {children}
        </code>
      );
    }
  }), []);

  const viewContent = useCallback((edType:string, edContent:string) => {
    if (edType === "html") {
      return (
        <div key={"viewer-div-content-" + (props?.attrs?.key || props.id)} id={"viewer-div-content-" + (props?.attrs?.key || props.id)}
          dangerouslySetInnerHTML={{__html: cleanHtml(edContent || "")}}
        />
      );
    }
    if (edType === "md") {
      return (
        <Markdown key={"viewer-div-content-" + (props?.attrs?.key || props.id)}
          remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={codeComponents}
        >
          {edContent || ""}
        </Markdown>
      );
    }
    return (
      <div key={"viewer-div-content-" + (props?.attrs?.key || props.id)} id={"viewer-div-content-" + (props?.attrs?.key || props.id)}>
        {edContent || ""}
      </div>
    );
  }, [codeComponents, props.id, props?.attrs?.key]);

  if (props.hasStyleSizeVariable && (props?.attrs?.key || props.id)) {
    return (
      <div
        {...props}
        key={"viewer-div-wrapper-" + (props?.attrs?.key || props.id)} id={"viewer-div-wrapper-" + (props?.attrs?.key || props.id)}
        className={props.className} style={styl}
      >
        <style>
          { // @ts-ignore
            ":root{--" + props.id + "-height: " + (props?.style?.height ?? 0) + "; --" + props.id + "-width: " + (props?.style?.width ?? 0) + ";}"
          }
        </style>
        {viewContent(codeTp, props.value)}
      </div>
    );
  }

  return (
    <div
      key={"viewer-div-wrapper-" + (props?.attrs?.key || props.id)} id={"viewer-div-wrapper-" + (props?.attrs?.key || props.id)}
      className={props.className} style={styl}
    >
      {viewContent(codeTp, props.value)}
    </div>
  );
};

export default HTMLMDviewer;
